import setFieldValue from "@bevco/Store/Handlers/InstantRegistrationForm/SetFieldValue";
import { CustomActions, CustomState } from "@bevco/Store/Reducers";
import { colorStrings } from "@bevco/Utilities/variables";
import { OwnProps, setInstantFormEffects } from "@bevco/Widgets/CreateAccount/FormBaseComponent";
import { get } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { useEffect } from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => ({
    accountNumber: state.customInstantRegistrationForm.fieldValues.accountNumber,
    accountNumberApiError: state.customInstantRegistrationForm.fieldValues.accountNumberApiError,
});

const mapDispatchToProps = {
    setFieldValue,
};

export interface AccountNumberStyles {
    accountNumberLabel?: TextFieldProps;
    accountNumberTooltip?: TooltipPresentationProps;
}

export const accountNumberStyles: AccountNumberStyles = {
    accountNumberLabel: {
        cssOverrides: {
            formField: css`
                label {
                    display: flex;
                    align-items: center;
                }
            `,
        },
    },
    accountNumberTooltip: {
        cssOverrides: {
            tooltipWrapper: css`
                margin-left: 5px;

                button:hover,
                button:focus {
                    outline: none;
                }

                button svg {
                    color: ${getColor(colorStrings.secondaryMain)};
                }
            `,
        },
    },
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    DispatchProp<CustomActions>;

const AccountNumberWidget: React.FC<Props> = ({
    accountNumber,
    accountNumberApiError,
    setFieldValue: widgetSetFieldValue,
}) => {
    const label = translate("Account Number");
    const isRequired = true;
    const [fieldErrorMessage, setFieldErrorMessage] = React.useState("");

    const validateaccountNumber = async () => {
        let errorMessage: any = "";
        if (!accountNumber && isRequired) {
            errorMessage = siteMessage("InstantRegForm_AccountNumberIsRequiredErrorMessage", label);       
        } else if (accountNumber) {
            const validAccount = await validateAccount(accountNumber);
            if (!validAccount.IsValid) {
                errorMessage = validAccount.ErrorMessage;
            }
        } 

        setFieldErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    setInstantFormEffects(isRequired, accountNumber, "accountNumber", () => {
        if (isRequired && !accountNumber) return false;
        return fieldErrorMessage === "";
    });

    const fieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        widgetSetFieldValue({ key: "accountNumber", value: event.target.value });
        if (accountNumberApiError) {
            widgetSetFieldValue({ key: "accountNumberApiError", value: "" });
        }
        setFieldErrorMessage("");
    };

    const fieldBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateaccountNumber();
    };

    const validateAccount = async (accountNumber: string) => {
        const res = (await get(`api/v2/instantregistration/validateaccount/${accountNumber}`)) as any;
        return res.result;
    };

    return (
        <TextField
            label={
                <>
                    {label}
                    {" *"}
                    <Tooltip
                        {...accountNumberStyles.accountNumberTooltip}
                        text={siteMessage("accountNumber_TooltipMessage").toString()}
                    />
                </>
            }
            value={accountNumber || ""}
            error={accountNumberApiError || fieldErrorMessage}
            onChange={fieldChangeHandler}
            onBlur={fieldBlurHandler}
            id="accountNumber"
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountNumberWidget);
